import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextArea,
  TextInput,
  UntrustedContent,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime } from '../lib/format';

interface SourceVersion {
  id: string;
  version: number;
  checksum: string;
  reviewState: string;
  retrievedAt: string;
  byteSize: number;
  diffSummary: { injectionMarkers?: string[]; addedChars?: number; removedChars?: number } | null;
}

interface KnowledgeSource {
  id: string;
  type: string;
  name: string;
  uri: string | null;
  isTrusted: boolean;
  refreshEnabled: boolean;
  reviewState: string;
  currentVersion: number;
  lastCheckedAt: string | null;
  service: { serviceKey: string } | null;
  versions: SourceVersion[];
}

export function KnowledgePage({ role }: { role: string }) {
  const sources = useResource<{ items: KnowledgeSource[] }>('/api/admin/knowledge-sources');
  const services = useResource<{ items: Array<{ serviceKey: string; name: string }> }>(
    '/api/admin/services',
  );
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    serviceKey: '',
    type: 'MARKDOWN',
    name: '',
    uri: '',
    isTrusted: false,
    content: '',
  });
  const [viewing, setViewing] = useState<string | null>(null);
  const canWrite = role === 'OWNER' || role === 'ADMIN';

  const detail = useResource<{
    content: string;
    contentIsUntrusted: boolean;
    version: number;
    knowledgeSource: { name: string };
  }>(viewing ? `/api/admin/knowledge-source-versions/${viewing}` : null);

  return (
    <>
      <PageHeader
        title="Źródła wiedzy"
        description="Dokumentacja, którą agent może wyszukać. Każda wersja jest niezmienna, ma sumę kontrolną i wymaga przeglądu."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setOpen(true)}>
              Dodaj źródło
            </Button>
          ) : undefined
        }
      />

      {sources.loading ? (
        <Skeleton rows={5} />
      ) : (
        <Card tight>
          <DataTable
            rows={sources.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Brak źródeł wiedzy"
                description="Dodaj przewodnik w Markdown albo zaimportuj dokument OpenAPI."
              />
            }
            columns={[
              {
                key: 'name',
                header: 'Źródło',
                render: (row) => (
                  <>
                    <div>{row.name}</div>
                    {row.uri && (
                      <div
                        className="ah-mono"
                        style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}
                      >
                        {row.uri}
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: 'type',
                header: 'Typ',
                render: (row) => <Badge tone="info">{row.type}</Badge>,
              },
              {
                key: 'service',
                header: 'Usługa',
                render: (row) => <span className="ah-mono">{row.service?.serviceKey ?? '—'}</span>,
              },
              {
                key: 'trust',
                header: 'Zaufanie',
                render: (row) => (
                  <Badge tone={row.isTrusted ? 'ok' : 'warn'}>
                    {row.isTrusted ? 'kurowane' : 'niezaufane'}
                  </Badge>
                ),
              },
              {
                key: 'review',
                header: 'Przegląd',
                render: (row) => (
                  <Badge
                    tone={
                      row.reviewState === 'REVIEWED'
                        ? 'ok'
                        : row.reviewState === 'REJECTED'
                          ? 'danger'
                          : 'warn'
                    }
                  >
                    {row.reviewState}
                  </Badge>
                ),
              },
              {
                key: 'version',
                header: 'Wersja',
                align: 'right',
                render: (row) => row.currentVersion,
              },
              {
                key: 'checked',
                header: 'Sprawdzono',
                render: (row) => formatDateTime(row.lastCheckedAt),
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) => {
                  const latest = row.versions[0];
                  if (!latest) return null;
                  return (
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      {(latest.diffSummary?.injectionMarkers?.length ?? 0) > 0 && (
                        <Badge tone="danger">injection</Badge>
                      )}
                      <Button size="sm" onClick={() => setViewing(latest.id)}>
                        Podgląd
                      </Button>
                      {canWrite && latest.reviewState === 'PENDING' && (
                        <>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={async () => {
                              try {
                                await api.post(
                                  `/api/admin/knowledge-source-versions/${latest.id}/review`,
                                  { decision: 'REVIEWED' },
                                );
                                toast.success('Zatwierdzono wersję');
                                sources.reload();
                              } catch (error) {
                                const described = describeError(error);
                                toast.error(described.title, described.body);
                              }
                            }}
                          >
                            Zatwierdź
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={async () => {
                              try {
                                await api.post(
                                  `/api/admin/knowledge-source-versions/${latest.id}/review`,
                                  { decision: 'REJECTED' },
                                );
                                toast.success('Odrzucono — fragmenty usunięte z wyszukiwania');
                                sources.reload();
                              } catch (error) {
                                const described = describeError(error);
                                toast.error(described.title, described.body);
                              }
                            }}
                          >
                            Odrzuć
                          </Button>
                        </>
                      )}
                    </div>
                  );
                },
              },
            ]}
          />
        </Card>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Nowe źródło wiedzy"
        description="Treść jest sanityzowana i indeksowana, ale nigdy nie staje się instrukcją dla Hub."
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!form.name}
              onClick={async () => {
                try {
                  await api.post('/api/admin/knowledge-sources', {
                    ...(form.serviceKey ? { serviceKey: form.serviceKey } : {}),
                    type: form.type,
                    name: form.name,
                    ...(form.uri ? { uri: form.uri } : {}),
                    isTrusted: form.isTrusted,
                    refreshEnabled: false,
                    ...(form.content ? { content: form.content } : {}),
                  });
                  toast.success('Źródło zapisane', 'Wersja oczekuje na przegląd.');
                  setOpen(false);
                  setForm({
                    serviceKey: '',
                    type: 'MARKDOWN',
                    name: '',
                    uri: '',
                    isTrusted: false,
                    content: '',
                  });
                  sources.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Zapisz
            </Button>
          </>
        }
      >
        <Field label="Nazwa">
          {(id) => (
            <TextInput
              id={id}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          )}
        </Field>
        <Field label="Typ">
          {(id) => (
            <Select
              id={id}
              value={form.type}
              onChange={(event) => setForm({ ...form, type: event.target.value })}
            >
              <option value="MARKDOWN">Markdown (własna dokumentacja)</option>
              <option value="VENDOR_SNAPSHOT">Zrzut dokumentacji dostawcy</option>
              <option value="EXAMPLES">Przykłady</option>
              <option value="MANUAL_NOTE">Notatka ręczna</option>
            </Select>
          )}
        </Field>
        <Field label="Usługa">
          {(id) => (
            <Select
              id={id}
              value={form.serviceKey}
              onChange={(event) => setForm({ ...form, serviceKey: event.target.value })}
            >
              <option value="">— brak przypisania —</option>
              {(services.data?.items ?? []).map((service) => (
                <option key={service.serviceKey} value={service.serviceKey}>
                  {service.serviceKey}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Adres źródła" hint="Opcjonalny odnośnik do oryginału.">
          {(id) => (
            <TextInput
              id={id}
              value={form.uri}
              onChange={(event) => setForm({ ...form, uri: event.target.value })}
            />
          )}
        </Field>
        <Checkbox
          label="Treść kurowana przez nasz zespół"
          hint="Zaznacz wyłącznie dla dokumentacji, którą sami napisaliśmy. Materiały dostawców pozostają niezaufane."
          checked={form.isTrusted}
          onChange={(checked) => setForm({ ...form, isTrusted: checked })}
        />
        <Field label="Treść (Markdown)">
          {(id) => (
            <TextArea
              id={id}
              rows={10}
              value={form.content}
              onChange={(event) => setForm({ ...form, content: event.target.value })}
            />
          )}
        </Field>
      </Modal>

      <Modal
        open={viewing !== null}
        onOpenChange={(value) => !value && setViewing(null)}
        title="Podgląd wersji"
        description={
          detail.data ? `${detail.data.knowledgeSource.name} — wersja ${detail.data.version}` : ''
        }
      >
        {detail.loading && <Skeleton rows={6} />}
        {detail.data &&
          (detail.data.contentIsUntrusted ? (
            <UntrustedContent source={detail.data.knowledgeSource.name}>
              <pre className="ah-code">{detail.data.content}</pre>
            </UntrustedContent>
          ) : (
            <pre className="ah-code">{detail.data.content}</pre>
          ))}
      </Modal>
    </>
  );
}
